import FileViewer from 'components/Grading/FileViewer';
import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import RubricRenderer from 'components/Grading/RubricRenderer';
import FeedbackRenderer from 'components/ViewSubmission/FeedbackRenderer';
import Header from 'components/ViewSubmission/Header';
import { isEqual } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    ButtonGroup,
    Container,
    Dropdown,
    Icon,
    IconButton,
    PDFViewer,
    SheetViewer,
    Stack,
    styled,
    Text,
    VideoViewer,
} from 'shell/components';
import { getS3FileURL } from 'utils/helper';

const Desktop = ({
    currentAssignment,
    currentSubmission,
    selectedCriterionTitle,
    highlights,
    tab,
    setTab,
    selectedCriterionId,
    setSelectedCriterionId,
    selectedFeedbackId,
    setSelectedFeedbackId,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isEditing,
    form,
    setForm,
    isCurrentFormValid,
    gradeOverrides,
    setGradeOverrides,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    onClickEdit,
    onClickEditCancel,
    onClickEditSave,
    onSelect,
    updateSubmission,
    handleCriterionNavigation,
}) => {
    const { t } = useTranslation('assignment');

    const url = useMemo(() => getS3FileURL(currentSubmission.files[0]), [currentSubmission.files]);

    const currentFile = currentSubmission.files[0];

    const renderableHighlights = highlights.map(({ id, value }) => {
        if (typeof value === 'string') return { id, value };
        return { id: id, index: value.index, value: value.highlight };
    });

    return (
        <CustomContainer>
            <Header />
            <Stack sx={isDocumentFullScreen ? { overflow: 'hidden' } : undefined} direction={'row'}>
                <LeftStack $isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && (
                        <BannerBox>
                            <Text color={'inherit'}>
                                {tab === 'grades' && selectedCriterionId
                                    ? t(
                                          'grading-section.document-preview.criterion-annotations',
                                          `Currently viewing annotations for the criterion "{{criterionTitle}}."`,
                                          {
                                              criterionTitle: selectedCriterionTitle,
                                          }
                                      )
                                    : tab === 'feedback' && selectedFeedbackId
                                      ? t(
                                            'grading-section.document-preview.feedback-annotations',
                                            `Currently viewing annotations for feedback.`
                                        )
                                      : t(
                                            'grading-section.document-preview.inline-comments-annotations',
                                            `Currently viewing annotations for inline comments.`
                                        )}
                            </Text>
                        </BannerBox>
                    )}
                    {currentFile.type === 'application/pdf' ? (
                        <PDFViewer
                            url={url}
                            highlights={renderableHighlights}
                            inlineComments={form?.inline_comments}
                            setInlineComments={(func) => {
                                if (isEqual(form, func(form))) return;
                                setForm(func);
                                updateSubmission(func(form));
                            }}
                            selectedInlineCommentId={selectedInlineCommentId}
                            setSelectedInlineCommentId={setSelectedInlineCommentId}
                            isFullScreen={isDocumentFullScreen}
                            setIsFullScreen={setIsDocumentFullScreen}
                        />
                    ) : currentFile.type === 'text/csv' ? (
                        <SheetViewer
                            highlights={renderableHighlights}
                            url={url}
                            isFullScreen={isDocumentFullScreen}
                            setIsFullScreen={setIsDocumentFullScreen}
                        />
                    ) : currentFile.type.startsWith('video/') ? (
                        <VideoViewer
                            highlights={renderableHighlights}
                            mimeType={currentFile.type}
                            transcript={currentFile.transcript}
                            url={url}
                        />
                    ) : (
                        <FileViewer docUrl={url} fileType={currentFile.type} />
                    )}
                </LeftStack>
                <RightStack $isDocumentFullScreen={isDocumentFullScreen}>
                    <ControlsStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        {!isEditing && (
                            <ButtonGroup
                                options={[
                                    {
                                        key: 'grades',
                                        value: t('common:ui.grades', 'Grades'),
                                        tooltip: t('common:ui.grades', 'Grades'),
                                    },
                                    {
                                        key: 'feedback',
                                        value: t('common:ui.feedback', 'Feedback'),
                                        disabled: isEditing,
                                        tooltip: t('common:ui.feedback', 'Feedback'),
                                    },
                                ]}
                                selected={tab}
                                onChange={setTab}
                                maxWidth={'60%'}
                            />
                        )}
                        {tab === 'grades' && (
                            <>
                                {isEditing ? (
                                    <Stack direction={'row'} spacing={2} sx={{ ml: 'auto' }}>
                                        <Button color={'neutral'} variant={'tertiary'} onClick={onClickEditCancel}>
                                            {t('common:controls.cancel', 'Cancel')}
                                        </Button>
                                        <Button disabled={!isCurrentFormValid} onClick={onClickEditSave}>
                                            {t('common:controls.save', 'Save')}
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Button
                                        color={'neutral'}
                                        startIcon={<Icon kind={'edit'} />}
                                        variant={'tertiary'}
                                        onClick={onClickEdit}
                                        sx={{ maxHeight: '44px' }}
                                    >
                                        {t('grading-section.edit-grades', 'Edit Grades')}
                                    </Button>
                                )}
                            </>
                        )}
                        {tab === 'feedback' && currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                            <Dropdown
                                id={'view-submission-more-options-dropdown'}
                                options={[
                                    {
                                        key: 'copy-all',
                                        value: t('grading-section.actions.copy-all-feedback', 'Copy all feedback'),
                                        icon: 'copy',
                                    },
                                    {
                                        key: 'delete',
                                        value: t('grading-section.actions.delete-all-feedback', 'Delete all feedback'),
                                        icon: 'delete',
                                    },
                                ]}
                                rootComponent={
                                    <IconButton
                                        aria-label={'more options'}
                                        kind={'moreHorizontal'}
                                        size={'medium'}
                                        variant={'square'}
                                    />
                                }
                                onSelect={onSelect}
                            />
                        )}
                    </ControlsStack>
                    {tab === 'grades' && (
                        <RubricRenderer
                            disabled={!isEditing}
                            showGraded={false}
                            showGradingExplanationInput={false}
                            showSuggestedTag={isEditing}
                            assignment={currentAssignment}
                            currentSubmission={currentSubmission}
                            locations={highlights}
                            form={form}
                            setForm={setForm}
                            gradeOverrides={gradeOverrides}
                            setGradeOverrides={setGradeOverrides}
                            selectedCriterionId={selectedCriterionId}
                            setSelectedCriterionId={setSelectedCriterionId}
                            setShowFailedDialog={() => {}}
                            handleCriterionNavigation={handleCriterionNavigation}
                            hideCriterionNavigation={isEditing}
                        />
                    )}
                    {tab === 'feedback' && (
                        <Stack sx={{ flex: 1, overflow: 'auto' }}>
                            {currentSubmission.is_failed || typeof currentSubmission.grading.feedback === 'string' ? (
                                <LegacyFeedbackRenderer
                                    currentSubmission={currentSubmission}
                                    form={form}
                                    setForm={setForm}
                                    setShowFailedDialog={() => {}}
                                />
                            ) : (
                                <FeedbackRenderer
                                    assignment={currentAssignment}
                                    currentSubmission={currentSubmission}
                                    locations={highlights}
                                    form={form}
                                    setForm={(func) => {
                                        setForm(func);
                                        updateSubmission(func(form));
                                    }}
                                    selectedFeedbackId={selectedFeedbackId}
                                    setSelectedFeedbackId={setSelectedFeedbackId}
                                    handleCriterionNavigation={handleCriterionNavigation}
                                />
                            )}
                        </Stack>
                    )}
                </RightStack>
            </Stack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '81px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const LeftStack = styled(Stack)(({ theme, $isDocumentFullScreen }) => {
    return {
        position: 'relative',
        width: $isDocumentFullScreen ? '100%' : '60%',
        height: 'calc(100vh - 81px)',
        transition: 'width 150ms',
        ...(!$isDocumentFullScreen && {
            borderRight: `1px solid ${theme.palette.ensightful.neutral[700]}`,
        }),
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const RightStack = styled(Stack)(({ theme, $isDocumentFullScreen }) => {
    return {
        width: $isDocumentFullScreen ? 0 : '40%',
        height: 'calc(100vh - 81px)',
        background: theme.palette.ensightful.shades[0],
        transition: 'width 150ms',
    };
});

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(3, 5),
        gap: theme.spacing(2),
    };
});

export default Desktop;
